import React, { useState, useEffect, useCallback } from 'react';
import './Marketplace.css';
import {
  getListings, formatOri, getWalletNfts, buildListTx, buildBuyTx, buildCancelTx,
} from '../../api/marketplaceService';
import { useWallet } from '../../contexts/WalletContext';
import WalletModal from '../../components/wallet/WalletModal';

/**
 * R42/R44 : Marketplace — vitrine + actions on-chain.
 * - Listings indexés (miroir serveur R41/R42, rafraîchi par l'indexeur 30 s).
 * - R44 : acheter / mettre en vente / annuler ON-CHAIN — le serveur construit
 *   la transaction, le wallet connecté signe (Wallet Standard) ; le wallet
 *   Elementum local signe via le serveur (devnet).
 */
const Marketplace = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [elementFilter, setElementFilter] = useState('ALL');
  const { connected, balance, signAndSend, getLocalSecret, refreshBalance } = useWallet();
  const [walletOpen, setWalletOpen] = useState(false);
  const [txMsg, setTxMsg] = useState(null); // { ok, text, sig }
  const [txBusy, setTxBusy] = useState(null); // listing en cours

  // ─── Mettre en vente ───
  const [sellOpen, setSellOpen] = useState(false);
  const [myNfts, setMyNfts] = useState([]);
  const [myNftsLoading, setMyNftsLoading] = useState(false);
  const [priceOri, setPriceOri] = useState('');
  const [selectedMint, setSelectedMint] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    const r = await getListings();
    if (r.success) setListings(r.listings);
    else setError(r.message || 'Marketplace indisponible');
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMyNfts = useCallback(async () => {
    if (!connected) return;
    setMyNftsLoading(true);
    const r = await getWalletNfts(connected.address);
    setMyNfts(r.nfts || []);
    setMyNftsLoading(false);
  }, [connected]);

  useEffect(() => { if (sellOpen) loadMyNfts(); }, [sellOpen, loadMyNfts]);

  /** Exécute une tx : wallet standard (client signe) ou local (serveur signe). */
  const runTx = useCallback(async (build /* (localSecret) => apiPromise */) => {
    setTxMsg(null);
    try {
      const secret = connected && connected.type === 'created' ? getLocalSecret() : null;
      if (connected && connected.type === 'created' && !secret) {
        setTxMsg({ ok: false, text: 'Secret local introuvable — réimportez votre wallet Elementum (bouton Connecter).', sig: null });
        return null;
      }
      const r = await build(secret);
      if (!r.success) {
        setTxMsg({ ok: false, text: r.message || 'Transaction refusée.', sig: null });
        return null;
      }
      let signature = r.signature || null;
      if (!signature && r.txBase64) {
        signature = await signAndSend(r.txBase64);
      }
      setTxMsg({ ok: true, text: 'Transaction confirmée on-chain ✓', sig: signature });
      load();
      refreshBalance(connected.address);
      return signature;
    } catch (e) {
      setTxMsg({ ok: false, text: e.message || 'Échec de la transaction.', sig: null });
      return null;
    }
  }, [connected, getLocalSecret, signAndSend, load, refreshBalance]);

  const handleBuy = async (l) => {
    if (!connected) { setWalletOpen(true); return; }
    if (!window.confirm(`Acheter ce NFT pour ${formatOri(l.priceMicro)} (+ taxe incluse) ? La transaction est atomique : vous recevez le NFT, le vendeur 96 %, la réserve 2 %, le burn 2 %.`)) return;
    setTxBusy(l.listing);
    await runTx((secret) => buildBuyTx(l.seller, l.nftMint, connected.address, secret));
    setTxBusy(null);
  };

  const handleCancel = async (l) => {
    setTxBusy(l.listing);
    await runTx((secret) => buildCancelTx(l.nftMint, l.seller, secret));
    setTxBusy(null);
  };

  const handleList = async () => {
    const micro = Math.round(parseFloat(priceOri.replace(',', '.')) * 1e6);
    if (!selectedMint || !micro || micro <= 0 || Number.isNaN(micro)) {
      setTxMsg({ ok: false, text: 'Choisissez un NFT et un prix valide en ORI.', sig: null });
      return;
    }
    const ok = await runTx((secret) => buildListTx(selectedMint, micro, connected.address, secret));
    if (ok) { setSellOpen(false); setPriceOri(''); setSelectedMint(null); }
  };

  const elements = ['ALL', ...new Set(listings.map((l) => l.card && l.card.element).filter(Boolean))];
  const filtered = elementFilter === 'ALL'
    ? listings
    : listings.filter((l) => l.card && l.card.element === elementFilter);
  filtered.sort((a, b) => (a.priceMicro || 0) - (b.priceMicro || 0));

  return (
    <div className="mkt-page">
      <header className="mkt-header">
        <h1><span className="mkt-title-glyph" aria-hidden="true">🏛️</span> Marketplace <span className="mkt-chain-badge" title="Escrow on-chain — programme Solana marketplace (devnet)">⛓️ On-chain</span></h1>
        <p className="mkt-sub">
          Cartes NFT en escrow. Une carte listée est verrouillée : elle ne peut
          pas être utilisée en deck <strong>classé</strong> tant que la vente est active.
        </p>
        <div className="mkt-actions">
          {connected ? (
            <>
              <button className="mkt-refresh sell" onClick={() => setSellOpen(true)}>🏷️ Mettre en vente</button>
              <span className="mkt-wallet-pill" onClick={() => setWalletOpen(true)} title={connected.address}>
                👛 {connected.address.slice(0, 4)}…{connected.address.slice(-4)}
                {balance !== null && <strong> · {balance.toFixed(2)} SOL</strong>}
              </span>
            </>
          ) : (
            <button className="mkt-refresh" onClick={() => setWalletOpen(true)}>👛 Connecter un wallet</button>
          )}
          <button className="mkt-refresh" onClick={load} disabled={loading}>
            {loading ? '⟳ Chargement…' : '⟳ Rafraîchir'}
          </button>
        </div>
      </header>

      {walletOpen && <WalletModal onClose={() => setWalletOpen(false)} />}

      {sellOpen && connected && (
        <div className="mkt-sell-panel">
          <h3>🏷️ Mettre un NFT en vente</h3>
          {myNftsLoading ? <p>Chargement de vos NFT…</p> : (
            myNfts.length === 0
              ? <p className="mkt-hint">Aucun NFT détenu par ce wallet (les NFT possèdent une supply de 1).</p>
              : (
                <>
                  <div className="mkt-nft-picker">
                    {myNfts.map((n) => (
                      <button
                        key={n.mint}
                        className={`mkt-nft-chip ${selectedMint === n.mint ? 'selected' : ''}`}
                        onClick={() => setSelectedMint(n.mint)}
                        title={n.mint}
                      >
                        🖼️ {n.mint.slice(0, 6)}…{n.mint.slice(-4)}
                      </button>
                    ))}
                  </div>
                  <div className="mkt-price-row">
                    <input
                      type="text"
                      inputMode="decimal"
                      placeholder="Prix en ORI (ex. 150)"
                      value={priceOri}
                      onChange={(e) => setPriceOri(e.target.value)}
                    />
                    <button className="mkt-refresh sell" onClick={handleList}>Lister (escrow 🔒)</button>
                  </div>
                  <p className="mkt-hint">Le NFT quitte votre wallet pour l'escrow PDA — il revient si vous annulez (0 frais) ou part à l'acheteur (taxe 4 %).</p>
                </>
              )
          )}
          <button className="mkt-close-panel" onClick={() => setSellOpen(false)}>Fermer ✕</button>
        </div>
      )}

      {txMsg && (
        <div className={`mkt-txmsg ${txMsg.ok ? 'ok' : 'ko'}`}>
          {txMsg.ok ? '✅' : '⚠️'} {txMsg.text}
          {txMsg.sig && (
            <a href={`https://explorer.solana.com/tx/${txMsg.sig}?cluster=devnet`} target="_blank" rel="noopener noreferrer"> Voir ↗</a>
          )}
        </div>
      )}

      {error && <div className="mkt-error">⚠️ {error}</div>}

      {!loading && !error && listings.length === 0 && (
        <div className="mkt-empty">
          <span className="mkt-empty-icon">🛒</span>
          <p>Aucune carte en vente pour le moment.</p>
          <p className="mkt-empty-hint">
            Les listings apparaissent ici dès qu'ils sont indexés depuis la chaîne
            {connected ? ' — soyez le premier à mettre un NFT en vente !' : '.'}
          </p>
        </div>
      )}

      {listings.length > 0 && (
        <div className="mkt-filters">
          {elements.map((el) => (
            <button
              key={el}
              className={`mkt-filter-btn ${elementFilter === el ? 'active' : ''} ${el !== 'ALL' ? `element-${el.toLowerCase()}` : ''}`}
              onClick={() => setElementFilter(el)}
            >
              {el === 'ALL' ? 'Tous' : el}
            </button>
          ))}
          <span className="mkt-count">{filtered.length} carte{filtered.length > 1 ? 's' : ''}</span>
        </div>
      )}

      <div className="mkt-grid">
        {filtered.map((l) => {
          const isMine = connected && l.seller === connected.address;
          return (
            <article key={l.listing} className={`mkt-card rarity-glow-${l.card ? l.card.rarity.toLowerCase() : 'common'}`}>
              <div className="mkt-art">
                {l.card ? (
                  <img
                    src={l.card.imageUrl || `/api/static/images/elements/${l.card.element.toLowerCase()}.svg`}
                    alt={l.card.name}
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = `/api/static/images/card-backs/${(l.card.element || 'fire').toLowerCase()}-back.svg`; }}
                  />
                ) : (
                  <div className="mkt-art-unknown" title={l.cardId}>❔</div>
                )}
                {l.card && <span className={`mkt-cost element-${l.card.element.toLowerCase()}`}>{l.card.cost}</span>}
                <span className="mkt-escrow-badge" title="NFT déposé dans l'escrow PDA — non jouable en classé">🔒 Escrow</span>
              </div>
              <div className="mkt-body">
                <h3>{l.card ? l.card.name : l.cardId}</h3>
                {l.card && (
                  <p className="mkt-meta">
                    <span className={`element-tag element-${l.card.element.toLowerCase()}`}>{l.card.element}</span>
                    <span className="mkt-rarity">{l.card.rarity}</span>
                  </p>
                )}
                <p className="mkt-price">{formatOri(l.priceMicro)}</p>
                <p className="mkt-seller" title={l.seller || ''}>
                  {isMine ? 'Votre vente' : `Vendeur : ${l.seller ? `${l.seller.slice(0, 4)}…${l.seller.slice(-4)}` : '—'}`}
                </p>
                {isMine ? (
                  <button className="mkt-btn cancel" disabled={txBusy === l.listing} onClick={() => handleCancel(l)}>
                    {txBusy === l.listing ? '⏳ Annulation…' : '↩️ Annuler la vente'}
                  </button>
                ) : (
                  <button className="mkt-btn buy" disabled={txBusy === l.listing} onClick={() => handleBuy(l)}>
                    {txBusy === l.listing ? '⏳ Signature…' : '🛒 Acheter on-chain'}
                  </button>
                )}
                {l.nftMint && (
                  <a
                    className="mkt-solscan"
                    href={`https://explorer.solana.com/address/${l.nftMint}?cluster=devnet`}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    Voir le NFT ↗
                  </a>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </div>
  );
};

export default Marketplace;
